import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import application from "../core/application";
import { Permission } from "../users/user.entity";
import { useModuleAccess } from "../settings/module-access";
import { appSettingsRepository } from "../settings/app-settings.repository";

type TourStep = {
  id: string;
  icon: JSX.Element;
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  scene: "dashboard" | "sell" | "products" | "customers" | "cash" | "reports";
};

const publicPaths = ["/", "/login", "/entrar", "/planos", "/assinar", "/checkout"];

const isPublicRoute = (pathname: string) => {
  if (publicPaths.includes(pathname)) return true;
  return pathname.startsWith("/checkout");
};

export function OnboardingTour() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const [user] = application.user.useState();
  const [bussiness] = application.bussiness.useState();
  const [appSettings] = application.appSettings.useState();
  const moduleAccess = useModuleAccess();
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [cashEnabled, setCashEnabled] = useState(!appSettings.cashRegisterDisabled);
  const [dismissed, setDismissed] = useState(false);

  const steps = useMemo<TourStep[]>(() => {
    const canSell = user?.hasPermission(Permission.SELL) ?? false;
    const canProducts = user?.hasPermission(Permission.WRITE_PRODUCTS) ?? false;
    const canCustomers = user?.hasPermission(Permission.MANAGE_CUSTOMERS) ?? false;
    const canStats = user?.hasPermission(Permission.READ_SALES_ANALYTICS) ?? false;
    const canCash = user?.hasPermission(Permission.CASH_REGISTER_MANAGEMENT) ?? false;
    const cashModule = moduleAccess.isEnabled("cash_register");

    return [
      {
        id: "dashboard",
        icon: <DashboardOutlinedIcon />,
        eyebrow: "Seu ponto de partida",
        title: "O painel reúne os atalhos do dia",
        description:
          "Depois do login, você chega numa visão simples para abrir venda, produto, cliente, caixa e relatórios sem procurar no menu.",
        bullets: ["Atalhos por permissão", "Menu lateral organizado", "Ações rápidas sempre por perto"],
        scene: "dashboard",
      },
      canSell && {
        id: "sell",
        icon: <PointOfSaleOutlinedIcon />,
        eyebrow: "Venda rápida",
        title: "Monte a venda em poucos cliques",
        description:
          "Busque produtos, ajuste quantidades, escolha pagamento e finalize. Dinheiro, Pix, cartão e fiado seguem para os relatórios corretos.",
        bullets: ["Busca por nome ou código", "Resumo do carrinho", "Finalização com formas de pagamento"],
        scene: "sell",
      },
      canProducts && {
        id: "products",
        icon: <Inventory2OutlinedIcon />,
        eyebrow: "Catálogo e estoque",
        title: "Produtos ficam prontos para vender",
        description:
          "Cadastre itens, categorias, fornecedores, preço e estoque. O cadastro em massa ajuda quando a loja já tem uma lista grande.",
        bullets: ["Preço e quantidade", "Categorias e fornecedores", "Importação em massa"],
        scene: "products",
      },
      canCustomers && {
        id: "customers",
        icon: <GroupsOutlinedIcon />,
        eyebrow: "Clientes e fiado",
        title: "Controle saldos sem perder histórico",
        description:
          "O módulo de clientes registra compras no fiado, pagamentos manuais e saldos em aberto para acompanhar quem ainda precisa acertar.",
        bullets: ["Saldo por cliente", "Histórico de transações", "Relatório de fiado"],
        scene: "customers",
      },
      canCash && cashModule && {
        id: "cash",
        icon: <AccountBalanceWalletOutlinedIcon />,
        eyebrow: "Controle de caixa",
        title: "Decida se quer usar abertura e fechamento",
        description:
          "O caixa acompanha dinheiro físico: troco inicial, vendas em dinheiro, entradas, retiradas e conferência no fechamento.",
        bullets: ["Sessão por turno", "Entradas e retiradas", "Diferença no fechamento"],
        scene: "cash",
      },
      canStats && {
        id: "reports",
        icon: <BarChartOutlinedIcon />,
        eyebrow: "Decisões com dados",
        title: "Relatórios mostram o movimento da loja",
        description:
          "Acompanhe vendas, formas de pagamento, desempenho por período e sinais importantes para agir antes que vire problema.",
        bullets: ["Receita por período", "Métodos de pagamento", "Produtos em destaque"],
        scene: "reports",
      },
    ].filter(Boolean) as TourStep[];
  }, [moduleAccess, user]);

  const open =
    !!user &&
    !!bussiness &&
    !moduleAccess.loading &&
    !appSettings.onboardingCompleted &&
    !dismissed &&
    !isPublicRoute(location.pathname) &&
    steps.length > 0;

  const current = steps[Math.min(stepIndex, steps.length - 1)];
  const progress = ((Math.min(stepIndex, steps.length - 1) + 1) / steps.length) * 100;
  const isLast = stepIndex >= steps.length - 1;

  const finish = async () => {
    setSaving(true);
    try {
      const updated = appSettings
        .withCashRegisterDisabled(!cashEnabled)
        .withOnboardingCompleted(true);
      await appSettingsRepository.saveConfig(updated);
      application.appSettings.set(updated);
      setDismissed(true);
    } finally {
      setSaving(false);
    }
  };

  if (!current) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          minHeight: { xs: "auto", sm: 560 },
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {current.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={800}>
            Tour rápido do Symplou
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 0.75, height: 6, borderRadius: 999 }}
          />
        </Box>
        <IconButton aria-label="Fechar tutorial" onClick={() => void finish()} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
            minHeight: { xs: "auto", sm: 470 },
          }}
        >
          <Stack sx={{ p: { xs: 2.5, sm: 4 } }} spacing={2.5} justifyContent="center">
            <Chip
              label={current.eyebrow}
              color="primary"
              variant="outlined"
              sx={{ alignSelf: "flex-start", fontWeight: 800 }}
            />
            <Box>
              <Typography variant="h4" fontWeight={900} letterSpacing="-0.02em" sx={{ fontSize: { xs: "1.2rem", md: "2.125rem" } }}>
                {current.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25 }}>
                {current.description}
              </Typography>
            </Box>
            <Stack spacing={1}>
              {current.bullets.map((bullet) => (
                <Stack key={bullet} direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                  <Typography variant="body2" fontWeight={700}>
                    {bullet}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {current.scene === "cash" ? (
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: 1,
                  borderColor: cashEnabled ? "primary.main" : "divider",
                  borderRadius: 2,
                  bgcolor: cashEnabled
                    ? alpha(theme.palette.primary.main, 0.07)
                    : alpha(theme.palette.text.primary, 0.03),
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Switch
                    checked={cashEnabled}
                    onChange={(event) => setCashEnabled(event.target.checked)}
                    color="primary"
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={800}>
                      Manter controle de caixa {cashEnabled ? "ativado" : "desativado"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Você pode mudar isso depois em Configurações.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : null}
          </Stack>

          <Box
            sx={{
              bgcolor: "#0f172a",
              color: "#f8fafc",
              p: { xs: 2, sm: 3 },
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <OnboardingScene scene={current.scene} cashEnabled={cashEnabled} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: "divider" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={stepIndex === 0 || saving}
          onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Voltar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => void finish()}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Pular
        </Button>
        <Button
          variant="contained"
          endIcon={isLast ? <DoneAllIcon /> : <ArrowForwardIcon />}
          disabled={saving}
          onClick={() => {
            if (isLast) {
              void finish();
              return;
            }
            setStepIndex((prev) => Math.min(steps.length - 1, prev + 1));
          }}
          sx={{ fontWeight: 900 }}
        >
          {isLast ? "Começar" : "Próximo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OnboardingScene({
  scene,
  cashEnabled,
}: {
  scene: TourStep["scene"];
  cashEnabled: boolean;
}) {
  if (scene === "sell") return <SellScene />;
  if (scene === "products") return <ProductsScene />;
  if (scene === "customers") return <CustomersScene />;
  if (scene === "cash") return <CashScene enabled={cashEnabled} />;
  if (scene === "reports") return <ReportsScene />;
  return <DashboardScene />;
}

const panelSx = {
  width: "min(100%, 420px)",
  borderRadius: 3,
  border: "1px solid rgba(148,163,184,0.18)",
  bgcolor: "rgba(15,23,42,0.92)",
  boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
  p: 2,
  "@keyframes tour-rise": {
    "0%": { opacity: 0, transform: "translateY(18px) scale(0.98)" },
    "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  animation: "tour-rise 420ms ease-out",
};

function MiniHeader({ title }: { title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#2dd4bf" }} />
      <Typography variant="caption" fontWeight={900} sx={{ color: "#e2e8f0" }}>
        {title}
      </Typography>
    </Stack>
  );
}

function DashboardScene() {
  return (
    <Box sx={panelSx}>
      <MiniHeader title="Painel inicial" />
      <Stack spacing={1.25}>
        <Box sx={{ height: 72, borderRadius: 2, bgcolor: "rgba(45,212,191,0.14)", p: 1.5 }}>
          <Box sx={{ width: "45%", height: 12, borderRadius: 1, bgcolor: "#ccfbf1", mb: 1 }} />
          <Box sx={{ width: "70%", height: 8, borderRadius: 1, bgcolor: "rgba(204,251,241,0.5)" }} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
          {["Venda", "Produtos", "Caixa", "Clientes", "Relatórios", "Config"].map((item, idx) => (
            <Box
              key={item}
              sx={{
                height: 70,
                borderRadius: 2,
                bgcolor: "rgba(248,250,252,0.08)",
                p: 1,
                animation: `tour-rise 420ms ease-out ${idx * 70}ms both`,
              }}
            >
              <Box sx={{ width: 22, height: 22, borderRadius: 1.2, bgcolor: "#2dd4bf", mb: 1 }} />
              <Typography variant="caption" fontWeight={800}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}

function SellScene() {
  return (
    <Box sx={panelSx}>
      <MiniHeader title="Venda em andamento" />
      <Stack spacing={1}>
        {["Café especial", "Pão de queijo", "Suco natural"].map((item, idx) => (
          <Stack
            key={item}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: "rgba(248,250,252,0.08)",
              animation: `tour-rise 360ms ease-out ${idx * 100}ms both`,
            }}
          >
            <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: idx === 0 ? "#38bdf8" : "#2dd4bf" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight={900}>{item}</Typography>
              <Box sx={{ width: "55%", height: 6, borderRadius: 1, bgcolor: "rgba(226,232,240,0.35)" }} />
            </Box>
            <Typography variant="caption" fontWeight={900}>R$ {(idx + 1) * 8},90</Typography>
          </Stack>
        ))}
        <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: "#14b8a6", color: "#042f2e" }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={900}>Finalizar</Typography>
            <Typography variant="body2" fontWeight={900}>R$ 53,70</Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function ProductsScene() {
  return (
    <Box sx={panelSx}>
      <MiniHeader title="Catálogo" />
      <Stack spacing={1}>
        {["Código", "Nome do produto", "Preço", "Estoque"].map((item, idx) => (
          <Box key={item} sx={{ animation: `tour-rise 340ms ease-out ${idx * 90}ms both` }}>
            <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.65)" }}>{item}</Typography>
            <Box
              sx={{
                mt: 0.5,
                height: 34,
                borderRadius: 1.5,
                bgcolor: "rgba(248,250,252,0.08)",
                border: "1px solid rgba(148,163,184,0.14)",
              }}
            />
          </Box>
        ))}
        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
          <Chip size="small" label="Categoria" sx={{ bgcolor: "#bae6fd", fontWeight: 800 }} />
          <Chip size="small" label="Fornecedor" sx={{ bgcolor: "#ccfbf1", fontWeight: 800 }} />
        </Stack>
      </Stack>
    </Box>
  );
}

function CustomersScene() {
  return (
    <Box sx={panelSx}>
      <MiniHeader title="Clientes" />
      <Stack spacing={1.2}>
        {[
          ["Ana Souza", "Saldo R$ 86,40"],
          ["Mercado Boa Vista", "Pago hoje"],
          ["João Lima", "Saldo R$ 24,90"],
        ].map(([name, detail], idx) => (
          <Stack
            key={name}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: "rgba(248,250,252,0.08)",
              animation: `tour-rise 360ms ease-out ${idx * 90}ms both`,
            }}
          >
            <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: idx === 1 ? "#a7f3d0" : "#fef3c7" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight={900}>{name}</Typography>
              <Typography variant="caption" display="block" sx={{ color: "rgba(226,232,240,0.68)" }}>
                {detail}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function CashScene({ enabled }: { enabled: boolean }) {
  return (
    <Box sx={panelSx}>
      <MiniHeader title={enabled ? "Caixa ativo" : "Caixa oculto"} />
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: enabled ? "rgba(20,184,166,0.16)" : "rgba(148,163,184,0.12)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" fontWeight={900}>Sessão de hoje</Typography>
          <Chip size="small" label={enabled ? "Aberto" : "Desativado"} sx={{ bgcolor: enabled ? "#99f6e4" : "#cbd5e1", fontWeight: 900 }} />
        </Stack>
        <Typography variant="h4" fontWeight={900} sx={{ mt: 2 }}>
          {enabled ? "R$ 342,50" : "Pronto para vender"}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
        {["Entrada", "Retirada", "Fechar"].map((item, idx) => (
          <Box
            key={item}
            sx={{
              flex: 1,
              height: 54,
              borderRadius: 2,
              bgcolor: enabled ? "rgba(248,250,252,0.08)" : "rgba(148,163,184,0.06)",
              opacity: enabled ? 1 : 0.42,
              animation: `tour-rise 360ms ease-out ${idx * 80}ms both`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" fontWeight={900}>{item}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function ReportsScene() {
  return (
    <Box sx={panelSx}>
      <MiniHeader title="Relatórios" />
      <Stack direction="row" alignItems="end" spacing={1.2} sx={{ height: 180, px: 1 }}>
        {[46, 92, 62, 130, 104, 150, 118].map((height, idx) => (
          <Box
            key={height}
            sx={{
              flex: 1,
              height,
              borderRadius: "10px 10px 4px 4px",
              bgcolor: idx % 2 === 0 ? "#2dd4bf" : "#38bdf8",
              animation: `tour-rise 420ms ease-out ${idx * 70}ms both`,
            }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Chip size="small" label="Pix 42%" sx={{ bgcolor: "#ccfbf1", fontWeight: 900 }} />
        <Chip size="small" label="Cartão 31%" sx={{ bgcolor: "#bae6fd", fontWeight: 900 }} />
        <Chip size="small" label="Dinheiro 27%" sx={{ bgcolor: "#fef3c7", fontWeight: 900 }} />
      </Stack>
    </Box>
  );
}
